import React from "react";

import { useFrame } from "../../shared/frame";
import { usePrefersReducedMotion } from "../../shared/reducedMotion";

/** The prototype's count-up length (proof.js). */
const COUNT_UP_MS = 1150;

/**
 * Counts from 0 to `to` each time `counting` turns true, eased `1-(1-p)³` so
 * it decelerates into the target and lands on it exactly. At rest, and when
 * `counting` starts out true (server render), it shows `to`. Under reduced
 * motion, including when it is switched on mid-count, it shows `to` at once.
 */
export const useCountUp = (to: number, counting: boolean) => {
    const reduced = usePrefersReducedMotion();
    const [prev, setPrev] = React.useState(counting);
    const [live, setLive] = React.useState(false);
    const [value, setValue] = React.useState(to);
    const start = React.useRef<number | null>(null);

    if (counting !== prev) {
        setPrev(counting);
        setLive(counting);
        if (counting) setValue(0);
    }

    React.useEffect(() => {
        if (!live) start.current = null;
    }, [live]);

    useFrame((now) => {
        start.current ??= now;
        const p = Math.min(1, (now - start.current) / COUNT_UP_MS);
        setValue(Math.round(to * (1 - Math.pow(1 - p, 3))));
        if (p >= 1) setLive(false);
    }, live && !reduced);

    return live && !reduced ? value : to;
};
